import { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import type {
  FacetCount,
  GraphData,
  GraphLens,
  GraphNode,
  ViewportAction,
  ViewportCommand,
} from "../types";

const SEARCH_SEPARATOR = /\s+/;

function normalizeSearchValue(value: string) {
  return value.trim().toLocaleLowerCase("ko-KR");
}

function nodeMatchesQuery(node: GraphNode, query: string) {
  const terms = normalizeSearchValue(query).split(SEARCH_SEPARATOR).filter(Boolean);
  if (terms.length === 0) return true;

  const searchable = normalizeSearchValue(
    [node.title, node.claim, node.why, node.type, ...node.topics, ...node.tags].join(" "),
  );
  return terms.every((term) => searchable.includes(term));
}

function toggleSetValue(current: Set<string>, value: string) {
  const next = new Set(current);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

const GRAPH_LENSES: GraphLens[] = ["type", "topic", "origin", "label"];

interface ExplorerUrlState {
  activeTopics: Set<string>;
  activeTypes: Set<string>;
  lens: GraphLens;
  query: string;
  selectedNodeId: string | null;
}

function readExplorerUrlState(data: GraphData): ExplorerUrlState {
  if (typeof window === "undefined") {
    return {
      activeTopics: new Set(),
      activeTypes: new Set(),
      lens: "type",
      query: "",
      selectedNodeId: null,
    };
  }

  const params = new URLSearchParams(window.location.search);
  const topicIds = new Set(data.nodes.flatMap((node) => node.topics));
  const typeIds = new Set(data.nodes.map((node) => node.type));
  const nodeIds = new Set(data.nodes.map((node) => node.id));
  const lensParam = params.get("lens");

  return {
    activeTopics: new Set(params.getAll("topic").filter((topic) => topicIds.has(topic))),
    activeTypes: new Set(params.getAll("type").filter((type) => typeIds.has(type))),
    lens: lensParam && GRAPH_LENSES.includes(lensParam as GraphLens) ? (lensParam as GraphLens) : "type",
    query: params.get("q") ?? "",
    selectedNodeId:
      params.get("node") && nodeIds.has(params.get("node") ?? "") ? params.get("node") : null,
  };
}

export function useGraphExplorer(data: GraphData, reducedMotion: boolean) {
  const [initialUrlState] = useState(() => readExplorerUrlState(data));
  const [query, setQueryState] = useState(() => initialUrlState.query);
  const deferredQuery = useDeferredValue(query);
  const [activeTopics, setActiveTopics] = useState<Set<string>>(() => initialUrlState.activeTopics);
  const [activeTypes, setActiveTypes] = useState<Set<string>>(() => initialUrlState.activeTypes);
  const [highlightedEdgeType, setHighlightedEdgeType] = useState<string | null>(null);
  const [lens, setLensState] = useState<GraphLens>(() => initialUrlState.lens);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(
    () => initialUrlState.selectedNodeId,
  );
  const hasInitialExplorerState =
    initialUrlState.query.trim().length > 0 ||
    initialUrlState.activeTopics.size > 0 ||
    initialUrlState.activeTypes.size > 0 ||
    initialUrlState.lens !== "type" ||
    initialUrlState.selectedNodeId !== null;
  const [autoRotate, setAutoRotate] = useState(
    () => !reducedMotion && !hasInitialExplorerState,
  );
  const [viewportCommand, setViewportCommand] = useState<ViewportCommand>({
    id: 0,
    action: "reset",
  });
  const [nodeListOpen, setNodeListOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (reducedMotion) setAutoRotate(false);
  }, [reducedMotion]);

  const nodeById = useMemo(
    () => new Map(data.nodes.map((node) => [node.id, node] as const)),
    [data.nodes],
  );

  const topicFacets = useMemo<FacetCount[]>(() => {
    const counts = new Map<string, number>();
    for (const node of data.nodes) {
      for (const topic of node.topics) counts.set(topic, (counts.get(topic) ?? 0) + 1);
    }

    return Array.from(counts, ([id, count]) => ({ id, label: id, count }))
      .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label, "ko"))
      .slice(0, 12);
  }, [data.nodes]);

  const typeFacets = useMemo<FacetCount[]>(() => {
    const counts = new Map<string, number>();
    for (const node of data.nodes) counts.set(node.type, (counts.get(node.type) ?? 0) + 1);

    return data.ontology.nodeTypes
      .map((type) => ({
        id: type.id,
        label: type.label,
        count: counts.get(type.id) ?? 0,
        color: type.color,
        description: type.description,
      }))
      .filter((type) => type.count > 0);
  }, [data.nodes, data.ontology.nodeTypes]);

  const filteredNodes = useMemo(() => {
    if (activeTopics.size === 0 && activeTypes.size === 0) return data.nodes;

    return data.nodes.filter((node) => {
      const topicMatches =
        activeTopics.size === 0 || node.topics.some((topic) => activeTopics.has(topic));
      const typeMatches = activeTypes.size === 0 || activeTypes.has(node.type);
      return topicMatches && typeMatches;
    });
  }, [activeTopics, activeTypes, data.nodes]);

  const visibleNodeIds = useMemo(
    () => new Set(filteredNodes.map((node) => node.id)),
    [filteredNodes],
  );

  useEffect(() => {
    if (selectedNodeId && !visibleNodeIds.has(selectedNodeId)) setSelectedNodeId(null);
  }, [selectedNodeId, visibleNodeIds]);

  const filteredEdges = useMemo(
    () =>
      data.edges.filter(
        (edge) => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target),
      ),
    [data.edges, visibleNodeIds],
  );

  const searchResults = useMemo(
    () => filteredNodes.filter((node) => nodeMatchesQuery(node, deferredQuery)),
    [deferredQuery, filteredNodes],
  );

  const searchMatchIds = useMemo(
    () => new Set(searchResults.map((node) => node.id)),
    [searchResults],
  );

  const selectedNode = selectedNodeId ? (nodeById.get(selectedNodeId) ?? null) : null;

  const focusNodeIds = useMemo(() => {
    if (!selectedNodeId) return new Set<string>();

    const focused = new Set<string>([selectedNodeId]);
    for (const edge of data.edges) {
      if (edge.source === selectedNodeId) focused.add(edge.target);
      if (edge.target === selectedNodeId) focused.add(edge.source);
    }
    return focused;
  }, [data.edges, selectedNodeId]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    params.delete("q");
    if (query.trim()) params.set("q", query);
    params.delete("topic");
    [...activeTopics].sort().forEach((topic) => params.append("topic", topic));
    params.delete("type");
    [...activeTypes].sort().forEach((type) => params.append("type", type));
    params.delete("lens");
    if (lens !== "type") params.set("lens", lens);
    params.delete("node");
    if (selectedNodeId && nodeById.has(selectedNodeId)) params.set("node", selectedNodeId);

    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (nextUrl !== currentUrl) window.history.replaceState(null, "", nextUrl);
  }, [activeTopics, activeTypes, lens, nodeById, query, selectedNodeId]);

  const pauseAutoRotate = useCallback(() => {
    setAutoRotate(false);
  }, []);

  const setQuery = useCallback(
    (value: string) => {
      pauseAutoRotate();
      setQueryState(value);
    },
    [pauseAutoRotate],
  );

  const setLens = useCallback(
    (nextLens: GraphLens) => {
      pauseAutoRotate();
      setLensState(nextLens);
    },
    [pauseAutoRotate],
  );

  const toggleTopic = useCallback((topic: string) => {
    pauseAutoRotate();
    setActiveTopics((current) => toggleSetValue(current, topic));
  }, [pauseAutoRotate]);

  const toggleType = useCallback((type: string) => {
    pauseAutoRotate();
    setActiveTypes((current) => toggleSetValue(current, type));
  }, [pauseAutoRotate]);

  const toggleEdgeTypeHighlight = useCallback((type: string) => {
    pauseAutoRotate();
    setHighlightedEdgeType((current) => (current === type ? null : type));
  }, [pauseAutoRotate]);

  const clearEdgeTypeHighlight = useCallback(() => {
    pauseAutoRotate();
    setHighlightedEdgeType(null);
  }, [pauseAutoRotate]);

  const selectNode = useCallback((nodeId: string | null) => {
    pauseAutoRotate();
    setSelectedNodeId(nodeId);
    if (nodeId) setNodeListOpen(false);
  }, [pauseAutoRotate]);

  const resetFilters = useCallback(() => {
    pauseAutoRotate();
    setQueryState("");
    setActiveTopics(new Set());
    setActiveTypes(new Set());
    setHighlightedEdgeType(null);
    setLensState("type");
    setSelectedNodeId(null);
    setViewportCommand((command) => ({ id: command.id + 1, action: "reset" }));
  }, [pauseAutoRotate]);

  const sendViewportCommand = useCallback((action: ViewportAction) => {
    setViewportCommand((command) => ({ id: command.id + 1, action }));
  }, []);

  return {
    activeTopics,
    activeTypes,
    autoRotate,
    clearEdgeTypeHighlight,
    deferredQuery,
    filteredEdges,
    filteredNodes,
    focusNodeIds,
    highlightedEdgeType,
    lens,
    nodeById,
    nodeListOpen,
    query,
    searchMatchIds,
    searchResults,
    selectedNode,
    selectedNodeId,
    sidebarOpen,
    topicFacets,
    typeFacets,
    viewportCommand,
    resetFilters,
    selectNode,
    sendViewportCommand,
    setAutoRotate,
    setLens,
    setNodeListOpen,
    setQuery,
    setSidebarOpen,
    toggleTopic,
    toggleType,
    toggleEdgeTypeHighlight,
  };
}
